/**
 * AI Configuration using Firebase Functions (secure server-side Gemini API)
 * All AI calls go through Firebase Functions - no client-side API key needed
 */
import { functions } from './firebase-config.js';
import { httpsCallable } from 'firebase/functions';
import { updateCachedCredits } from './credits-service.js';

// Check if AI is available (Firebase Functions are always available if authenticated)
export function isAIAvailable() {
    return true; // AI is always available via Firebase Functions for Pro users
}

// Helper to handle AI function responses
function handleAIResponse(result) {
    if (result.data.creditsRemaining !== undefined) {
        updateCachedCredits(result.data.creditsRemaining);
    }
    return result.data;
}

// Helper to handle AI errors
function handleAIError(error, operation) {
    console.error(`AI ${operation} error:`, error);

    // Extract error message from Firebase Functions error
    if (error.code === 'functions/resource-exhausted') {
        throw new Error('You have no AI credits remaining. Purchase more credits to continue.');
    }
    if (error.code === 'functions/failed-precondition') {
        throw new Error('AI features require a Pro subscription. Please upgrade.');
    }
    if (error.code === 'functions/unauthenticated') {
        throw new Error('Please sign in to use AI features.');
    }

    throw new Error(error.message || `Failed to ${operation}. Please try again.`);
}

// AI Feature: Summarize note content
export async function summarizeNote(content) {
    try {
        const callable = httpsCallable(functions, 'summarizeNote');
        const result = await callable({ content });
        return handleAIResponse(result).summary;
    } catch (error) {
        handleAIError(error, 'summarize');
    }
}

// AI Feature: Continue writing - preserves formatting
export async function continueWriting(content, lastParagraph, customPrompt = null, userDirection = null) {
    try {
        const callable = httpsCallable(functions, 'continueWriting');
        const result = await callable({
            content,
            lastParagraph,
            customPrompt,
            userDirection
        });
        return handleAIResponse(result).text;
    } catch (error) {
        handleAIError(error, 'continue writing');
    }
}

// AI Feature: Improve writing - intelligent content restructuring
export async function improveWriting(selectedHTML, customPrompt = null) {
    try {
        const callable = httpsCallable(functions, 'improveWriting');
        const result = await callable({
            content: selectedHTML,
            customPrompt
        });
        return handleAIResponse(result).improved;
    } catch (error) {
        handleAIError(error, 'improve writing');
    }
}

// AI Feature: Expand ideas - preserves formatting
export async function expandIdeas(selectedText, fullContext, customPrompt = null, userDirection = null) {
    try {
        const callable = httpsCallable(functions, 'expandIdeas');
        const result = await callable({
            selectedText,
            fullContext,
            customPrompt,
            userDirection
        });
        return handleAIResponse(result).expanded;
    } catch (error) {
        handleAIError(error, 'expand ideas');
    }
}

// AI Feature: Auto-generate tags for a note
// Note: This is done client-side for now as it's a lighter operation
// Could be moved to Firebase Functions if needed
export async function generateTags(content) {
    // For now, return empty array - implement via Firebase Functions if needed
    console.log('generateTags called - not yet implemented via Firebase Functions');
    return [];
}

// AI Feature: Semantic search - find relevant notes
// Note: This is done client-side for now
export async function semanticSearch(query, notes) {
    // For now, return empty array - implement via Firebase Functions if needed
    console.log('semanticSearch called - not yet implemented via Firebase Functions');
    return [];
}
